/**
 * anamorphicPipeline — an anamorphic mirror (cylindrical projection).
 *
 * An anamorphic distortion is a stretched image that only resolves
 * to a recognizable picture when viewed from a specific angle or
 * reflected in a curved mirror. The classic example is the
 * Holbein "Ambassadors" painting where the distorted skull only
 * resolves when viewed from one side.
 *
 * Innovations:
 *   - We embed a "secret" portrait (a face-like shape) in the
 *     distortion, but the immediate view is a stretched blur.
 *   - A cylindrical mirror is drawn as a vertical reflective
 *     surface. The reflection of the distorted image appears
 *     undistorted in the mirror.
 *   - The "mirrored" view is computed by sampling the original
 *     image at a coordinate that depends on the cylinder's
 *     curvature.
 *   - Audio: bass increases the distortion amplitude, mid
 *     adjusts the cylinder position, high drives the mirror's
 *     reflectivity.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface AnamorphicPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.10, 0.08, 0.14), vec3(0.02, 0.02, 0.04), smoothstep(0.0, 0.7, r));
  vec3 col = bg;

  // === LEFT PANEL: The "distorted" original image ===
  // We draw a portrait that looks stretched/wrong when viewed flat.
  // It's a stylized face: a head shape, two eyes, a mouth.
  // When stretched horizontally (anamorphic distortion), it looks
  // elongated.
  vec2 leftP = p;
  // Map screen x to left panel (x in [-1, -0.05]).
  bool inLeft = leftP.x < -0.05;
  if (inLeft) {
    // The distorted face: stretched horizontally by a factor.
    float distort = 1.0 + 0.6 * bass;
    vec2 dp = vec2(leftP.x * distort + 0.5, leftP.y);
    // Head (ellipse).
    float headD = length(vec2(dp.x / 0.20, dp.y / 0.28)) - 1.0;
    float headMask = 1.0 - smoothstep(-0.02, 0.0, headD);
    col = mix(col, vec3(0.85, 0.65, 0.50), headMask * 0.9);
    // Hair on top.
    float hairD = length(vec2(dp.x / 0.22, (dp.y - 0.20) / 0.10)) - 1.0;
    float hairMask = 1.0 - smoothstep(-0.02, 0.0, hairD);
    col = mix(col, vec3(0.20, 0.10, 0.05), hairMask * 0.95);
    // Eyes.
    float eyeL = length(dp - vec2(-0.07, 0.03));
    float eyeR = length(dp - vec2(0.07, 0.03));
    col = mix(col, vec3(0.10, 0.05, 0.02), step(eyeL, 0.025));
    col = mix(col, vec3(0.10, 0.05, 0.02), step(eyeR, 0.025));
    // Mouth.
    float mouthD = length(dp - vec2(0.0, -0.13));
    float mouthW = 1.0 - smoothstep(0.0, 0.01, abs(mouthD - 0.05));
    col = mix(col, vec3(0.6, 0.10, 0.15), mouthW * step(0.0, mouthD - 0.045));
    // Outlines.
    float headEdge = 1.0 - smoothstep(0.0, 0.005, abs(headD));
    col = mix(col, vec3(0.15, 0.10, 0.05), headEdge * 0.6);
  }

  // === CENTER: A vertical cylindrical mirror ===
  // The mirror is at x = 0, width 0.08.
  // Inside the mirror, the reflection un-distorts the left image.
  // The reflection: for each pixel in the mirror, sample the left
  // image at a position that depends on the mirror curvature.
  // For a vertical cylinder mirror, the reflection maps the
  // current pixel (x, y) to (un-distorted_x, y) where
  // un-distorted_x = asin(x / radius) * (radius / pi) or similar.
  bool inMirror = abs(p.x) < 0.05;
  if (inMirror) {
    // Sample the distorted image at the un-distorted position.
    // The mirror maps current (x, y) to (x' = f(x), y) in the original.
    // For a vertical cylinder, the un-distortion is approximately
    // x' = x * mirrorCorrection where mirrorCorrection > 1.
    float mirrorC = 1.0 + 0.6 * bass;
    vec2 mp = vec2(p.x * mirrorC - 0.5, p.y);
    // Re-evaluate the face at mp.
    float headD = length(vec2(mp.x / 0.20, mp.y / 0.28)) - 1.0;
    float headMask = 1.0 - smoothstep(-0.02, 0.0, headD);
    col = mix(col, vec3(0.85, 0.65, 0.50), headMask * 0.9);
    float hairD = length(vec2(mp.x / 0.22, (mp.y - 0.20) / 0.10)) - 1.0;
    float hairMask = 1.0 - smoothstep(-0.02, 0.0, hairD);
    col = mix(col, vec3(0.20, 0.10, 0.05), hairMask * 0.95);
    float eyeL = length(mp - vec2(-0.07, 0.03));
    float eyeR = length(mp - vec2(0.07, 0.03));
    col = mix(col, vec3(0.10, 0.05, 0.02), step(eyeL, 0.025));
    col = mix(col, vec3(0.10, 0.05, 0.02), step(eyeR, 0.025));
    float mouthD = length(mp - vec2(0.0, -0.13));
    float mouthW = 1.0 - smoothstep(0.0, 0.01, abs(mouthD - 0.05));
    col = mix(col, vec3(0.6, 0.10, 0.15), mouthW * step(0.0, mouthD - 0.045));
    float headEdge = 1.0 - smoothstep(0.0, 0.005, abs(headD));
    col = mix(col, vec3(0.15, 0.10, 0.05), headEdge * 0.6);
    // Mirror surface tint.
    col = mix(col, vec3(0.7, 0.85, 1.0), 0.10);
    // Mirror edges (vertical lines).
    float edgeL = 1.0 - smoothstep(0.0, 0.003, abs(p.x + 0.05));
    float edgeR = 1.0 - smoothstep(0.0, 0.003, abs(p.x - 0.05));
    col = mix(col, vec3(0.95, 0.95, 1.0), (edgeL + edgeR) * 0.5);
  }

  // === RIGHT PANEL: The "ground truth" face for comparison ===
  bool inRight = p.x > 0.05;
  if (inRight) {
    vec2 rp = vec2(p.x - 0.5, p.y);
    float headD = length(vec2(rp.x / 0.20, rp.y / 0.28)) - 1.0;
    float headMask = 1.0 - smoothstep(-0.02, 0.0, headD);
    col = mix(col, vec3(0.85, 0.65, 0.50), headMask * 0.9);
    float hairD = length(vec2(rp.x / 0.22, (rp.y - 0.20) / 0.10)) - 1.0;
    float hairMask = 1.0 - smoothstep(-0.02, 0.0, hairD);
    col = mix(col, vec3(0.20, 0.10, 0.05), hairMask * 0.95);
    float eyeL = length(rp - vec2(-0.07, 0.03));
    float eyeR = length(rp - vec2(0.07, 0.03));
    col = mix(col, vec3(0.10, 0.05, 0.02), step(eyeL, 0.025));
    col = mix(col, vec3(0.10, 0.05, 0.02), step(eyeR, 0.025));
    float mouthD = length(rp - vec2(0.0, -0.13));
    float mouthW = 1.0 - smoothstep(0.0, 0.01, abs(mouthD - 0.05));
    col = mix(col, vec3(0.6, 0.10, 0.15), mouthW * step(0.0, mouthD - 0.045));
    float headEdge = 1.0 - smoothstep(0.0, 0.005, abs(headD));
    col = mix(col, vec3(0.15, 0.10, 0.05), headEdge * 0.6);
  }

  // Panel dividers.
  float div1 = 1.0 - smoothstep(0.0, 0.003, abs(p.x + 0.05));
  float div2 = 1.0 - smoothstep(0.0, 0.003, abs(p.x - 0.05));

  col = mix(col, vec3(0.7, 0.7, 0.85), max(div1, div2) * 0.5);

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeAnamorphicBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeAnamorphicRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createAnamorphicPipeline(gl: WebGL2RenderingContext): AnamorphicPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawAnamorphic(
  gl: WebGL2RenderingContext,
  pipe: AnamorphicPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
